import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { getSiteTexts, saveSiteTexts, loadSiteData, type SiteTexts } from '@/lib/store';

// Default texts for every editable string on the site.
export const defaultSiteTexts: SiteTexts = {
  'hero.kicker': 'Chodzieski Klub Żeglarski LOK • Chodzież / Jezioro Miejskie',
  'hero.title': 'ChKŻ LOK',
  'hero.subtitle': 'Wiatr, woda, żagle',
  'hero.fullName': 'Chodzieski Klub Żeglarski Ligi Obrony Kraju',
  'hero.tagline': 'Żeglarstwo — sztuka o randze życia i życie o randze sztuki',
  'hero.cta1': 'Poznaj nas',
  'hero.cta2': 'Kontakt',
  'about.label': 'Klub',
  'about.title': 'Chodzieski Klub Żeglarski LOK',
  'about.text':
    'Jesteśmy klubem sportowym działającym w Chodzieży nad Jeziorem Miejskim. Prowadzimy treningi żeglarskie i szkolimy dzieci oraz młodzież w żeglarstwie.',
  'sailing.label': 'Żeglarstwo',
  'sailing.title': 'Życie na wodzie',
  'sailing.text':
    'Treningi, szkolenia i przygody na Jeziorze Miejskim. Jak sami piszecie na Instagramie: „Trening czyni mistrza! Brawa dla młodych żeglarzy!”',
  'news.label': 'Aktualności',
  'news.title': 'Aktualności',
  'news.text': 'Najnowsze posty i wydarzenia klubu.',
  'lake.label': 'Jezioro / Chodzież',
  'lake.title': 'Jezioro Miejskie — nasz dom',
  'lake.text':
    'Codziennie wypływamy na wodę — treningi, regaty i pikniki żeglarskie to nasz codzienny rytuał na Jeziorze Miejskim. Wspólnie z rodzicami i członkami klubu ćwiczymy żeglarstwo, rywalizujemy i bawiąc się razem tworzymy niezapomniane chwile.',
  'lake.btnGallery': 'Zobacz galerię',
  'lake.btnNews': 'Zobacz aktualności',
  'gallery.label': 'Galeria',
  'gallery.title': 'Żeglarskie chwile',
  'contact.label': 'Kontakt',
  'contact.title': 'Dołącz do nas na wodzie',
  'contact.text':
    'Klub prowadzi treningi i szkolenia żeglarskie dla dzieci i młodzieży. Napisz do nas, aby dowiedzieć się więcej o naborze.',
  'footer.title': 'Do zobaczenia',
  'footer.subtitle': 'na wodzie',
  'footer.rights': 'Wszelkie prawa zastrzeżone.',
};

// Label displayed in the admin panel for each key.
export const siteTextLabels: Record<string, string> = {
  'hero.kicker': 'Hero — nadtytuł',
  'hero.title': 'Hero — tytuł',
  'hero.subtitle': 'Hero — podtytuł',
  'hero.fullName': 'Hero — pełna nazwa klubu',
  'hero.tagline': 'Hero — motto klubu',
  'hero.cta1': 'Hero — przycisk 1',
  'hero.cta2': 'Hero — przycisk 2 (Kontakt)',
  'about.label': 'Klub — nadtytuł',
  'about.title': 'Klub — tytuł',
  'about.text': 'Klub — opis',
  'sailing.label': 'Żeglarstwo — nadtytuł',
  'sailing.title': 'Żeglarstwo — tytuł',
  'sailing.text': 'Żeglarstwo — opis',
  'news.label': 'Aktualności — nadtytuł',
  'news.title': 'Aktualności — tytuł',
  'news.text': 'Aktualności — opis',
  'lake.label': 'Jezioro — nadtytuł',
  'lake.title': 'Jezioro — tytuł',
  'lake.text': 'Jezioro — opis',
  'lake.btnGallery': 'Jezioro — przycisk galeria',
  'lake.btnNews': 'Jezioro — przycisk aktualności',
  'gallery.label': 'Galeria — nadtytuł',
  'gallery.title': 'Galeria — tytuł',
  'contact.label': 'Kontakt — nadtytuł',
  'contact.title': 'Kontakt — tytuł',
  'contact.text': 'Kontakt — opis',
  'footer.title': 'Stopka — tytuł',
  'footer.subtitle': 'Stopka — podtytuł',
  'footer.rights': 'Stopka — prawa zastrzeżone',
};

interface SiteTextContextValue {
  texts: SiteTexts;
  t: (key: string) => string;
  setText: (key: string, value: string) => void;
  save: () => void;
  reset: () => void;
}

const SiteTextContext = createContext<SiteTextContextValue | null>(null);

export function SiteTextProvider({ children }: { children: ReactNode }) {
  const [texts, setTexts] = useState<SiteTexts>(() => ({
    ...defaultSiteTexts,
    ...getSiteTexts(),
  }));

  // Load shared site data from the DS-style backend (/api/site-data) — what
  // every visitor reads. This makes admin edits visible to everyone instantly
  // after publishing (opcja 3: live API), no redeploy needed.
  useEffect(() => {
    let alive = true;
    loadSiteData()
      .then((data) => {
        if (!alive) return;
        setTexts((prev) => ({ ...defaultSiteTexts, ...prev, ...(data.texts ?? {}) }));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const t = useCallback((key: string) => texts[key] ?? defaultSiteTexts[key] ?? '', [texts]);

  const setText = useCallback((key: string, value: string) => {
    setTexts((prev) => ({ ...prev, [key]: value }));
  }, []);

  const save = useCallback(() => {
    saveSiteTexts(texts);
  }, [texts]);

  const reset = useCallback(() => {
    const defaults = { ...defaultSiteTexts };
    setTexts(defaults);
    saveSiteTexts(defaults);
  }, []);

  return (
    <SiteTextContext.Provider value={{ texts, t, setText, save, reset }}>
      {children}
    </SiteTextContext.Provider>
  );
}

export function useSiteText(): SiteTextContextValue {
  const ctx = useContext(SiteTextContext);
  if (!ctx) {
    // Fallback (no provider) — return defaults
    return {
      texts: defaultSiteTexts,
      t: (key: string) => defaultSiteTexts[key] ?? '',
      setText: () => {},
      save: () => {},
      reset: () => {},
    };
  }
  return ctx;
}
